import {AfterViewInit, Component} from '@angular/core';
import {AdminService} from '../../../../service/admin.service';
import {AdministratorApiModel} from '../../../../model/administrator-api.model';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {DatePipe, NgIf} from '@angular/common';
import {MatIconButton} from '@angular/material/button';
import {
  MatCell,
  MatCellDef,
  MatColumnDef, MatFooterCell, MatFooterCellDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow, MatRowDef, MatTable
} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../../dialog/confirmation/confirmation.component';
import {MatBadge} from '@angular/material/badge';
import {SettingsAdminDialogComponent} from '../../../dialog/admin/settings/settings.component';
import {MatSnackBar} from '@angular/material/snack-bar';

/**
 * @description Administrators management page
 * @class ManageAdminPageComponent
 * @export
 * @name ManageAdminPageComponent
 */
@Component({
  imports: [
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatIcon,
    NgIf,
    DatePipe,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTooltip,
    MatHeaderCellDef,
    MatFooterCell,
    MatFooterCellDef,
    MatIconButton,
    MatBadge
  ],
  selector: 'app-page-admin-manage',
  standalone: true,
  styleUrl: './manage.component.scss',
  templateUrl: './manage.component.html'
})
export class ManageAdminPageComponent implements AfterViewInit {

  /**
   * @description This property contains the administrators registered in the system.
   * @memberof ManageAdminPageComponent
   * @name ManageAdminPageComponent#administrators
   * @property
   * @protected
   * @type {AdministratorApiModel[]}
   */
  protected administrators: AdministratorApiModel[] = [];

  /**
   * @description This property contains the information for the current administrator.
   * @memberof ManageAdminPageComponent
   * @name ManageAdminPageComponent#currentAdministrator
   * @property
   * @protected
   * @type {AdministratorApiModel}
   */
  protected currentAdministrator!: AdministratorApiModel;

  /**
   * @description This property contains the loading status of the administrators.
   * @memberof ManageAdminPageComponent
   * @name ManageAdminPageComponent#loading
   * @property
   * @protected
   * @type {boolean}
   */
  protected loading: boolean = false;

  /**
   * @description This property contains the columns for the administrators table.
   * @memberof ManageAdminPageComponent
   * @name ManageAdminPageComponent#tableColumns
   * @property
   * @protected
   * @type {string[]}
   */
  protected tableColumns: string[] = [
    '_select', 'emailAddress', 'name', 'status', 'created', 'modified', '_actions'
  ];

  /**
   * @constructor
   * @description This method instantiates a new instance of our administrator information component.
   * @memberof ManageAdminPageComponent
   * @name ManageAdminPageComponent#constructor
   * @param {AdminService} adminService The injected instance of our admin service provider.
   * @param {MatDialog} dialog The injected instance of the Angular Material dialog service.
   * @param {MatSnackBar} snackbar The injected instance of the Angular Material snack bar service.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the administrator service provider.
     * @memberof ManageAdminPageComponent
     * @name ManageAdminPageComponent#adminService
     * @param {AdminService} adminService The injected instance of our admin service provider.
     * @private
     * @property
     * @readonly
     * @type {AdminService}
     */
    private readonly adminService: AdminService,
    /**
     * @description This property contains the Angular Material dialog service.
     * @memberof ManageAdminPageComponent
     * @name ManageAdminPageComponent#dialog
     * @private
     * @property
     * @readonly
     * @type {MatDialog}
     */
    private readonly dialog: MatDialog,
    /**
     * @description This property contains the Angular Material snack bar service.
     * @memberof ManageAdminPageComponent
     * @name ManageAdminPageComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar
  ) {
  }

  /**
   * @description This method is called when the user confirms the deletion of an administrator.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#deleteAdministrator()
   * @param {AdministratorApiModel} administrator The administrator to delete.
   * @private
   */
  private deleteAdministrator(administrator: AdministratorApiModel): void {

    // Subscribe to the admin service delete method.
    this.adminService.delete(administrator.emailAddress).subscribe({

      // Handle the completion by reloading the administrators.
      complete: () => {

        // Display a success message.
        this.snackbar.open(`Administrator ${administrator.firstName} ${administrator.lastName} deleted successfully.`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-success'],
            verticalPosition: 'bottom'
          });

        // Reload the administrators.
        this.loadAdministrators()
      },

      // Handle the error.
      error: (e: Error) => this.snackbar.open(`Deleting Administrator ${administrator.firstName} ${administrator.lastName} failed with:  ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        })
    });
  }

  /**
   * @description This method is called when the user confirms the deletion of multiple administrators.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#deleteAdministrators()
   * @param {AdministratorApiModel[]} administrators The administrator to delete.
   * @private
   */
  private deleteAdministrators(administrators: AdministratorApiModel[]): void {

    // Subscribe to the admin service delete method.
    this.adminService.deleteMany(administrators).subscribe({

      // Handle the completion by reloading the administrators.
      complete: () => {

        // Display a success message.
        this.snackbar.open(`Deleted ${administrators.length} administrators successfully.`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-success'],
            verticalPosition: 'bottom'
          });

        // Reload the administrators.
        this.loadAdministrators()
      },

      // Handle the error.
      error: (e: Error) => this.snackbar.open(`Deleting ${administrators.length} administrators failed with:  ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        })
    });
  }

  /**
   * @description This method loads the administrator details.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#loadAdministratorDetails
   * @private
   * @returns {void}
   */
  private loadAdministrators(): void {

    // Set the loading flag to true.
    this.loading = true;

    // Load the administrator details from the .NET backend API.
    this.adminService.list().subscribe({

      // Handle the completion of the request.
      complete: (): boolean => this.loading = false,

      // Handle any errors that occur.
      error: (e: Error) => this.snackbar.open(`Failed to List Administrators with:  ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        }),

      // Handle the next event.
      next: (a: AdministratorApiModel[]): AdministratorApiModel[] => this.administrators = a
    });
  }

  /**
   * @description This method presents the confirmation dialog when deleting an administrator.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#confirmDeleteAdministrator()
   * @param {AdministratorApiModel} administrator The administrator to delete.
   * @protected
   * @returns {void}
   */
  protected confirmDeleteAdministrator(administrator: AdministratorApiModel): void {

    // Localize our confirmation dialog reference.
    const confirmationDialog: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {

      // Define the data to be passed to the dialog.
      data: {

        // Set the message of the dialog.
        message: `Are you sure you want to delete ${administrator.firstName} ${administrator.lastName} (${administrator.emailAddress})?`,

        // Set the title of the dialog.
        title: 'Administrator Deletion'
      },

      // Define the width of the dialog.
      width: '350px'
    });

    // Bind to the close event of the confirmation dialog.
    confirmationDialog.afterClosed().subscribe((confirmed: boolean): void => {

      // Check if the user confirmed the deletion, then delete the administrator.
      if (confirmed) this.deleteAdministrator(administrator);
    });
  }

  /**
   * @description This method presents the confirmation dialog for deleting multiple administrators.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#confirmDeleteAdministrators()
   * @protected
   * @returns {void}
   */
  protected confirmDeleteAdministrators(): void {

    // Count the selected administrators.
    const selectedAdministrators: AdministratorApiModel[] = this.administrators
      .filter(a => a.emailAddress !== this.currentAdministrator.emailAddress && a.selected);

    // Check if no administrators were selected, then return if none were.
    if (selectedAdministrators.length === 0) return;

    // Localize our confirmation dialog reference.
    const confirmationDialog: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {

      // Define the data to be passed to the dialog.
      data: {

        // Set the message of the dialog.
        message: `Are you sure you want to delete the ${selectedAdministrators.length} selected administrator(s)?`,

        // Set the title of the dialog.
        title: 'Multiple Administrator Deletion'
      },

      // Define the width of the dialog.
      width: '350px'
    });

    // Bind to the close event of the confirmation dialog.
    confirmationDialog.afterClosed().subscribe((confirmed: boolean): void => {

      // Check if the user confirmed the deletion, then delete the selected administrators.
      if (confirmed) this.deleteAdministrators(selectedAdministrators);
    });
  }

  /**
   * @description This method presents the confirmation dialog for locking multiple administrators.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#confirmLockAdministrators()
   * @protected
   * @returns {void}
   */
  protected confirmLockAdministrators(): void {

    // Count the selected administrators.
    const selectedAdministrators = this.administrators.filter(a =>
      a.emailAddress !== this.currentAdministrator.emailAddress && a.selected && !a.locked).length;

    // Check if no administrators were selected, then return if none were.
    if (selectedAdministrators === 0) return;

    // Localize our confirmation dialog reference.
    const confirmationDialog: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {

      // Define the data to be passed to the dialog.
      data: {

        // Set the message of the dialog.
        message: `Are you sure you want to lock the ${selectedAdministrators} selected administrator(s)?`,

        // Set the title of the dialog.
        title: 'Multiple Administrator Lock'
      },

      // Define the width of the dialog.
      width: '350px'
    });

    // Bind to the close event of the confirmation dialog.
    confirmationDialog.afterClosed().subscribe((confirmed: boolean): void => {

      // Check if the user confirmed the deletion.
      if (confirmed) {

        // Perform the deletion logic here.
        console.log('Locking selected administrators...');
      }
    });
  }

  /**
   * @description This method presents the confirmation dialog for unlocking multiple administrators.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#confirmUnlockAdministrators()
   * @protected
   * @returns {void}
   */
  protected confirmUnlockAdministrators(): void {

    // Count the selected administrators.
    const selectedAdministrators = this.administrators.filter(a =>
      a.emailAddress !== this.currentAdministrator.emailAddress && a.selected && a.locked).length;

    // Check if no administrators were selected, then return if none were.
    if (selectedAdministrators === 0) return;

    // Localize our confirmation dialog reference.
    const confirmationDialog: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {

      // Define the data to be passed to the dialog.
      data: {

        // Set the message of the dialog.
        message: `Are you sure you want to unlock the ${selectedAdministrators} selected administrator(s)?`,

        // Set the title of the dialog.
        title: 'Multiple Administrator Unlock'
      },

      // Define the width of the dialog.
      width: '350px'
    });

    // Bind to the close event of the confirmation dialog.
    confirmationDialog.afterClosed().subscribe((confirmed: boolean): void => {

      // Check if the user confirmed the deletion.
      if (confirmed) {

        // Perform the deletion logic here.
        console.log('Unlocking selected administrators...');
      }
    });
  }

  /**
   * @description This method is called when the user clicks the "Modify" button for an administrator or "Add New Administrator."
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#modifyAdministrator()
   * @param {AdministratorApiModel} [administrator] The administrator to modify.
   * @param {boolean} [passwordRequired] A flag indicating whether the password is required or not.
   * @protected
   * @returns {void}
   */
  protected modifyAdministrator(administrator?: AdministratorApiModel, passwordRequired: boolean = true): void {

    // Open our dialog for modifying the administrator.
    this.dialog.open(SettingsAdminDialogComponent, {

      // Define the data to be passed to the dialog.
      data: {

        // Pass the administrator to the dialog.
        administrator,

        // We'll want to enable the toggles for this form.
        disableToggles: false,

        // Pass the new flag to the dialog.
        isNew: !administrator,

        // Pass the password-required flag to the dialog.
        passwordRequired
      },

      // Prevent the dialog from being closed by clicking outside of it.
      disableClose: true,

      // Define the width of the dialog.
      width: '1024px'
    }).afterClosed().subscribe((a?: AdministratorApiModel): void => {

      // Reload the administrators table if an administrator was returned.
      if (!!a) this.loadAdministrators()
    });
  }

  /**
   * @description This method is called when the user clicks on the checkbox to select a specific administrator.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#toggleAdministratorSelection()
   * @param {AdministratorApiModel} administrator
   * @param {boolean} selected
   * @protected
   * @returns {void}
   */
  protected toggleAdministratorSelection(administrator: AdministratorApiModel, selected: boolean): void {

    // Reset the selected flag for the administrator.
    administrator.selected = selected;
  }

  /**
   * @description This method is called when the user clicks the "Select All" button.
   * @memberof ManageAdminPageComponent
   * @method
   * @name ManageAdminPageComponent#toggleAllAdministratorsSelection()
   * @param {boolean} selected A flag indicating whether the administrators should be selected or not.
   * @protected
   * @returns {void}
   */
  protected toggleAllAdministratorsSelection(selected: boolean): void {

    // Iterate over each of the administrators.
    this.administrators.forEach((a: AdministratorApiModel): void => {

      // Reset the selected flag for the administrator, if it's not the same as the currently authenticated administrator.
      if (a.emailAddress !== this.currentAdministrator.emailAddress) a.selected = selected;
    });
  }

  /**
   * @abstract
   * @description This method is called after Angular has fully initialized the component's view.
   * @memberof ManageAdminPageComponent
   * @name ManageAdminPageComponent#ngAfterViewInit
   * @method
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Load the details of the current administrator.
    this.adminService.details().subscribe((a: AdministratorApiModel): void => {

      // Reset the current administrator object.
      this.currentAdministrator = a;

      // Load the administrator details from the server.
      this.loadAdministrators();
    });
  }
}
